import { zodResolver } from '@hookform/resolvers/zod';
import { useDisclosure } from '@nextui-org/react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { updateServerSchema, UpdateServerSchema, FrontendServerSchema } from '@common/types';
import { TypographyUI } from '@portal/components/common/Typography.ui';
import { ServerFormUI } from '@portal/components/Server.Form.ui';
import { tsrestClient } from '@portal/provider/tsrest';

export const EditServer = ({
  serverData,
  clearServerData,
}: {
  serverData: FrontendServerSchema;
  clearServerData: () => void;
}) => {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const [currentData, setCurrentData] = React.useState(serverData);

  const serversQuery = tsrestClient.servers.get.useQuery(['servers'], {
    headers: {
      authorization: localStorage.getItem('token') || '',
    },
  });
  const statsQuery = tsrestClient.serversStats.get.useQuery(['servers-stats'], {
    headers: {
      authorization: localStorage.getItem('token') || '',
    },
  });
  const updateServerMutation = tsrestClient.servers.update.useMutation({
    onSettled: (_, err) => {
      if (!err) {
        onClose();
      }
      serversQuery.refetch();
      statsQuery.refetch();
    },
    onError: (e, variables) => {
      toast(
        <>
          <TypographyUI className='font-bold'>Cannot update server &apos;{variables.body.name}&apos;</TypographyUI>
          <TypographyUI className='text-ts'>error: {(e.body as any).message}</TypographyUI>
        </>,
        {
          type: 'error',
        }
      );
    },
    onSuccess: (_, variables) => {
      toast(`Server '${variables.body.name}' is successfully updated`, { type: 'info' });
    },
  });

  const updateServerForm = useForm<UpdateServerSchema>({
    resolver: zodResolver(updateServerSchema),
    mode: 'all',
    defaultValues: serverData,
  });
  const {
    control,
    register,
    formState: { errors, isSubmitting, isValid, isDirty },
    handleSubmit,
    reset,
  } = updateServerForm;

  useEffect(() => {
    if (serverData.uuid === '') return;
    setCurrentData(serverData);

    // Show __KEEP_PASSWORD__ in password field to indicate no change
    const formDataWithKeepPassword = {
      ...serverData,
      sshPassword: '__KEEP_PASSWORD__',
    };

    reset(formDataWithKeepPassword);
    onOpen();
    clearServerData();
  }, [serverData]);

  const onSubmit = async (data: UpdateServerSchema) => {
    // Handle password update logic
    // If password field contains __KEEP_PASSWORD__, it will be sent as-is to backend
    // If it contains a different value, the new password will be sent
    const updatedData = {
      ...data,
      sshPassword: data.sshPassword,
    };

    updateServerMutation.mutate({
      params: { uuid: currentData.uuid },
      body: updatedData,
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });
  };

  return (
    <ServerFormUI
      modalVisible={isOpen}
      onModalVisibleChange={onOpenChange}
      control={control}
      register={register}
      errors={errors}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitValid={!(!isValid || !isDirty)}
      isUpdate
    />
  );
};
